import React from 'react';
import Icon from '../Icon';

interface LinkedRecordRowProps {
  /** Where the row navigates. Omit to render an inert row — used when the
   *  related record has no detail route of its own. */
  href?: string;
  /** The record's own id, shown first and in bold (e.g. "PO-014", "GS-033"). */
  id?: string;
  /** What the record is, shown after the id (vendor name, guest name, …).
   *  Nullable because plenty of these come straight off a nullable column;
   *  a missing one drops the separator rather than leaving "PO-014 · ". */
  label?: string | null;
  /** Anything further worth showing on the row, e.g. a marketplace order id. */
  meta?: string;
  /** Renders a detach control at the row's trailing edge. Omit for no control. */
  onDetach?: () => void;
  detachTitle?: string;
  detachDisabled?: boolean;
}

/**
 * One related record in a detail sidebar.
 *
 * The whole row is the link (matching the Detailed Comparison table's
 * "whole row is clickable" convention), with the detach control beside the
 * anchor rather than inside it — nesting a button in the anchor would fire
 * both the navigation and the detach on a single click.
 *
 * No trailing arrow: it read as a second, separate control next to the
 * detach button when the entire row was already the target. The hover
 * background and pointer carry the affordance instead.
 *
 * This started as `.sv-row--linked-order` in ShipmentsView.css and was used
 * only by the shipment page; the delivery page had grown its own key/value
 * version of the same idea. Both now render from here.
 */
export const LinkedRecordRow: React.FC<LinkedRecordRowProps> = ({
  href, id, label, meta, onDetach, detachTitle, detachDisabled,
}) => {
  const body = (
    <>
      <span>
        {id && <strong>{id}</strong>}
        {id && label ? ' · ' : ''}{label}
        {meta && <> · {meta}</>}
      </span>
    </>
  );

  return (
    <div className="ds-linked-row">
      {href
        ? <a href={href} className="ds-linked-row__link">{body}</a>
        : <span className="ds-linked-row__link ds-linked-row__link--inert">{body}</span>}
      {onDetach && (
        <button
          type="button"
          className="ds-linked-row__detach"
          title={detachTitle ?? 'Detach'}
          disabled={detachDisabled}
          onClick={onDetach}
        >
          <Icon name="trash" size={12} />
        </button>
      )}
    </div>
  );
};

export default LinkedRecordRow;
